import {
  Button,
  Checkbox,
  cn,
  Pagination,
  PaginationItemRenderProps,
} from "@heroui/react";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  RowSelectionState,
  getSortedRowModel,
  Row,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { DownloadButton } from "@/components/DownloadButton";

import DropDown from "./DropDown";
import FilterPopover from "./FilterPopover";

import { ActionItem } from "@/features/Table/Table";
import { useLocalStorage } from "@/lib/storage";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";

interface DataTableProps<TData, TValue> {
  table_name: string;
  label?: string;
  stats?: React.ReactNode;
  columns: ColumnDef<TData, TValue>[];
  filter?: React.ReactNode;
  new_item?: React.ReactNode;
  data: TData[];
  rowCount?: number;
  pageCount?: number;
  currentPage?: number;
  setCurrentPage?: React.Dispatch<React.SetStateAction<number>>;
  download_button?: boolean;
  columnVisibilityInit?: VisibilityState;
  actions?: ActionItem<TData>[];
  client?: string;
  enableRowSelection?: boolean;
  selectionActions?: (
    selectedRows: TData[],
    clearSelection: () => void,
  ) => React.ReactNode;
  pinnedLeft?: string[];
  getRowClassName?: (row: Row<TData>) => string | undefined;
  groupHeaderClassName?: string;
  tableClassName?: string;
  tableContainerClassName?: string;
  enableHorizontalScroll?: boolean;
  fillHeight?: boolean;
  pageSize?: number;
  toolbarClassName?: string;
}

export function DataTable<TData, TValue>({
  table_name,
  label,
  stats,
  columns,
  filter,
  new_item,
  data,
  rowCount,
  pageCount,
  currentPage,
  setCurrentPage,
  columnVisibilityInit,
  download_button = false,
  actions = [],
  client,
  enableRowSelection = false,
  selectionActions,
  pinnedLeft = [],
  getRowClassName,
  groupHeaderClassName,
  tableClassName,
  tableContainerClassName,
  enableHorizontalScroll = false,
  fillHeight = false,
  pageSize = 10,
  toolbarClassName,
}: DataTableProps<TData, TValue>) {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const [columnVisibility, setColumnVisibility] =
    useLocalStorage<VisibilityState>(
      `${table_name}-column-visibility`,
      columnVisibilityInit || {},
    );

  const toWorkspaceActionRoute = (rawRoute: string) => {
    const trimmed = rawRoute.trim();
    const absolute = trimmed.startsWith("/")
      ? trimmed
      : `/admin/${trimmed.replace(/^\/+/, "")}`;
    return toWorkspacePath(absolute);
  };

  const handlePageChange = (page: number) => {
    if (setCurrentPage) {
      setCurrentPage(page);
      return;
    }
    table.setPageIndex(page - 1);
  };

  const isServerPagination =
    typeof pageCount === "number" &&
    typeof currentPage === "number" &&
    typeof setCurrentPage === "function";

  useEffect(() => {
    if (enableRowSelection) {
      setRowSelection({});
    }
  }, [data, currentPage, enableRowSelection]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    manualPagination: isServerPagination,
    pageCount: isServerPagination ? pageCount : undefined,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    enableRowSelection,
    initialState: isServerPagination
      ? undefined
      : {
          pagination: {
            pageSize,
          },
        },
    getRowId: (row, index) => {
      const idValue = (row as Record<string, unknown>).id;
      return idValue !== undefined && idValue !== null
        ? String(idValue)
        : String(index);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const selectedRows = table
    .getSelectedRowModel()
    .rows.map((row) => row.original as TData);
  const clearSelection = () => setRowSelection({});
  const selectionActionNode =
    enableRowSelection && selectionActions
      ? selectionActions(selectedRows, clearSelection)
      : null;

  const pinnedLeftSet = useMemo(() => new Set(pinnedLeft), [pinnedLeft]);
  const pinnedLeftOffsets = useMemo(() => {
    const offsets = new Map<string, number>();
    let left = 0;
    if (enableRowSelection) {
      left += 40;
    }
    table.getVisibleLeafColumns().forEach((column) => {
      if (!pinnedLeftSet.has(column.id)) return;
      offsets.set(column.id, left);
      left += column.getSize();
    });
    return offsets;
  }, [table, pinnedLeftSet, enableRowSelection, columnVisibility, data.length]);
  const showToolbar =
    Boolean(label) ||
    Boolean(stats) ||
    Boolean(filter) ||
    Boolean(new_item) ||
    actions.length > 0 ||
    Boolean(selectionActionNode);
  const showFooter =
    isServerPagination &&
    typeof pageCount === "number" &&
    typeof rowCount === "number" &&
    typeof currentPage === "number" &&
    typeof client === "string";
  const headerGroupsAll = table.getHeaderGroups();
  const hasGroupedHeaders = headerGroupsAll.length > 1;
  const effectiveGroupHeaderClassName =
    groupHeaderClassName ??
    "bg-gray-100 text-gray-600 text-[11px] font-semibold text-center px-3 py-2 h-auto uppercase tracking-wider";
  const lastInGroupIds = useMemo(() => {
    const ids = new Set<string>();
    if (!hasGroupedHeaders) return ids;
    headerGroupsAll[0].headers.forEach((header) => {
      if (header.isPlaceholder) return;
      const leaves = header.getLeafHeaders();
      if (leaves.length > 0) ids.add(leaves[leaves.length - 1].id);
    });
    return ids;
  }, [hasGroupedHeaders, headerGroupsAll]);

  const translateTableTitle = (rawLabel: string) => {
    const tableKey = `${table_name}.title`;
    const translatedByTable = t(tableKey, {
      ns: "tables",
      defaultValue: rawLabel,
    });
    // If no table-specific translation exists yet, fall back to the raw label key.
    return translatedByTable === tableKey
      ? t(rawLabel, { defaultValue: rawLabel })
      : translatedByTable;
  };

  const translateHeader = (
    header: ReturnType<typeof table.getHeaderGroups>[number]["headers"][number],
    content: React.ReactNode,
  ): React.ReactNode => {
    if (typeof content !== "string") return content;
    const columnKey = `${table_name}.columns.${header.column.id}`;
    return t(columnKey, { ns: "tables", defaultValue: content });
  };

  return (
    <div className={fillHeight ? "flex flex-col flex-1 min-h-0" : "flex-auto"}>
      {showToolbar && (
        <header
          className={cn(
            "my-3 flex items-center justify-between",
            toolbarClassName,
          )}
        >
          <div className="flex min-w-0 items-center gap-3">
            {label ? (
              <h1 className="font-semibold text-xl text-primary">
                {translateTableTitle(label)}
              </h1>
            ) : (
              <div />
            )}
            {stats && (
              <span className="text-xs text-slate-500 tabular-nums">
                {stats}
              </span>
            )}
          </div>
          <div className="flex gap-2 items-center">
            {filter && <FilterPopover filter={filter} />}
            {new_item && new_item}
            <DropDown<TData> table={table} />
            {actions.length > 0 && (
              <div className="flex gap-2">
                {actions.map((action, index) =>
                  action.children ? (
                    <React.Fragment key={index}>
                      {action.children}
                    </React.Fragment>
                  ) : action.route ? (
                    <Link
                      key={index}
                      to={toWorkspaceActionRoute(action.route)}
                      className="flex items-center"
                    >
                      <Button
                        color={action.color || "primary"}
                        variant={action.variant || "flat"}
                        className="capitalize"
                        radius="full"
                        size="sm"
                      >
                        {action.label
                          ? t(action.label, { defaultValue: action.label })
                          : action.label}
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      key={index}
                      color={action.color || "primary"}
                      variant={action.variant || "flat"}
                      className="capitalize"
                      radius="full"
                      size="sm"
                      onPress={() => {
                        if (action.onClick) action.onClick(data);
                      }}
                    >
                      {action.label
                        ? t(action.label, { defaultValue: action.label })
                        : action.label}
                    </Button>
                  ),
                )}
              </div>
            )}
            {selectionActionNode && (
              <div className="flex gap-2">{selectionActionNode}</div>
            )}
          </div>
        </header>
      )}

      <div
        className={cn(
          "rounded-md",
          enableHorizontalScroll && "w-full overflow-x-auto",
          fillHeight && "flex-1 min-h-0 overflow-y-auto",
          tableContainerClassName,
        )}
      >
        <Table
          className={cn(
            "text-xs",
            enableHorizontalScroll && "min-w-max",
            tableClassName,
          )}
        >
          <TableHeader className="sticky top-0 z-20 border-b border-divider/40 bg-background">
            {headerGroupsAll.map((headerGroup, headerGroupIndex) => {
              const isGroupRow =
                hasGroupedHeaders &&
                headerGroupIndex < headerGroupsAll.length - 1;
              return (
                <TableRow
                  key={headerGroup.id}
                  className={
                    isGroupRow ? "border-0 hover:bg-transparent" : undefined
                  }
                >
                  {enableRowSelection &&
                    (isGroupRow ? (
                      <TableHead
                        className={cn("w-10", effectiveGroupHeaderClassName)}
                      />
                    ) : (
                      <TableHead className="w-10 sticky top-0 left-0 z-30 !bg-white">
                        <Checkbox
                          size="sm"
                          aria-label={t("table.selectAllRows", {
                            defaultValue: "Select all rows on this page",
                          })}
                          isSelected={table.getIsAllPageRowsSelected()}
                          isIndeterminate={
                            table.getIsSomePageRowsSelected() &&
                            !table.getIsAllPageRowsSelected()
                          }
                          onValueChange={(value) =>
                            table.toggleAllPageRowsSelected(value)
                          }
                          classNames={{
                            base: "w-full max-w-full justify-center cursor-pointer",
                            label: "sr-only",
                          }}
                        >
                          {t("table.selectAllRows", {
                            defaultValue: "Select all rows on this page",
                          })}
                        </Checkbox>
                      </TableHead>
                    ))}
                  {headerGroup.headers.map((header) => {
                    const leafHeaders = header.getLeafHeaders();
                    const lastLeafId = leafHeaders[leafHeaders.length - 1]?.id;
                    const isLastInGroup = isGroupRow
                      ? !header.isPlaceholder &&
                        lastInGroupIds.has(lastLeafId ?? "")
                      : lastInGroupIds.has(header.column.id);
                    return (
                      <TableHead
                        key={header.id}
                        // Required for grouped headers: keeps visual group spans aligned
                        // with the underlying leaf columns.
                        colSpan={header.colSpan}
                        className={cn(
                          isGroupRow && effectiveGroupHeaderClassName,
                          pinnedLeftSet.has(header.column.id) &&
                            "sticky top-0 z-30 !bg-white shadow-[1px_0_0_0_rgb(226_232_240)]",
                          isLastInGroup &&
                            (isGroupRow
                              ? "border-r-2 border-r-white/20"
                              : "border-r border-r-gray-200"),
                        )}
                        style={{
                          // For grouped headers, TanStack computes the effective size
                          // from child columns. Using getSize() keeps section widths aligned.
                          width: header.getSize(),
                          left: pinnedLeftOffsets.has(header.column.id)
                            ? `${pinnedLeftOffsets.get(header.column.id)}px`
                            : undefined,
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : translateHeader(
                              header,
                              flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              ) as React.ReactNode,
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={cn(
                    enableRowSelection && "data-[state=selected]:bg-primary/5",
                    getRowClassName?.(row),
                  )}
                >
                  {enableRowSelection && (
                    <TableCell
                      className="sticky left-0 z-20 bg-background"
                    >
                      <Checkbox
                        size="sm"
                        aria-label={t("table.selectRow", {
                          defaultValue: "Select row",
                        })}
                        isSelected={row.getIsSelected()}
                        onValueChange={(value) => row.toggleSelected(value)}
                        classNames={{
                          base: "w-full max-w-full justify-center cursor-pointer",
                          label: "sr-only",
                        }}
                      >
                        {t("table.selectRow", {
                          defaultValue: "Select row",
                        })}
                      </Checkbox>
                    </TableCell>
                  )}
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        pinnedLeftSet.has(cell.column.id) &&
                          cn(
                            "sticky z-10 !bg-white shadow-[1px_0_0_0_rgb(226_232_240)]",
                            getRowClassName?.(row),
                          ),
                        lastInGroupIds.has(cell.column.id) &&
                          "border-r border-r-gray-200",
                      )}
                      style={{
                        left: pinnedLeftOffsets.has(cell.column.id)
                          ? `${pinnedLeftOffsets.get(cell.column.id)}px`
                          : undefined,
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <NoResultsCell
                  colSpan={columns.length + (enableRowSelection ? 1 : 0)}
                />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {showFooter && (
        <Footer
          client={client!}
          table_name={table_name}
          pageCount={pageCount!}
          rowCount={rowCount!}
          currentPage={currentPage!}
          handlePageChange={handlePageChange}
          download_button={download_button}
        />
      )}
    </div>
  );
}

function NoResultsCell({ colSpan }: { colSpan: number }) {
  const { t } = useTranslation();
  return (
    <TableCell colSpan={colSpan} className="h-24 text-center">
      {t("table.noResults")}
    </TableCell>
  );
}

interface FooterProps {
  table_name: string;
  pageCount: number;
  rowCount: number;
  currentPage: number;
  handlePageChange: (page: number) => void;
  download_button: boolean;
  client: string;
}

function renderItem(
  props: PaginationItemRenderProps,
  t: (key: string) => string,
) {
  if (typeof props.value === "number" && props.isActive) {
    return (
      <button
        key={props.key}
        className={cn(props.className, "w-9 h-9 bg-primary text-white")}
        onClick={() => props.setPage?.(props.value as number)}
        aria-current={props.isActive ? "page" : undefined}
      >
        {props.value.toLocaleString()}
      </button>
    );
  }

  if (typeof props.value === "number") {
    return (
      <button
        key={props.key}
        ref={props.ref}
        className={cn(props.className, "w-9 h-9")}
        onClick={() => props.setPage?.(props.value as number)}
        aria-current={props.isActive ? "page" : undefined}
      >
        {props.value.toLocaleString()}
      </button>
    );
  }

  if (props.value === "dots") {
    return (
      <button
        key={props.key}
        className={cn(props.className, "w-9 h-9")}
        aria-label={t("pagination.morePages")}
      >
        ...
      </button>
    );
  }

  return undefined;
}

const Footer = ({
  table_name,
  pageCount,
  rowCount,
  currentPage,
  handlePageChange,
  download_button,
  client,
}: FooterProps) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const formattedRowCount = useMemo(
    () => rowCount.toLocaleString(i18n.language),
    [rowCount, i18n.language],
  );

  const downloadUrl = `${
    import.meta.env.VITE_BACKEND_URL
  }/${client}/${table_name}/download-excel?${params.toString()}`;

  const filename = `${table_name}_${new Date()
    .toISOString()
    .replace(/[:.]/g, "-")}.xlsx`;

  return (
    <div className="flex justify-between items-center py-5">
      {download_button ? (
        <DownloadButton
          href={downloadUrl}
          filename={filename}
          size="sm"
          variant="light"
          color="primary"
          textKey="actions.download"
        />
      ) : (
        <div />
      )}

      <div className="flex items-center gap-4">
        <span className="text-sm">
          {t("table.rowCount", {
            count: rowCount,
            formatted: formattedRowCount,
          })}
        </span>

        {pageCount > 1 && (
          <Pagination
            showControls
            total={pageCount}
            initialPage={1}
            page={currentPage}
            onChange={handlePageChange}
            size="sm"
            radius="md"
            color="primary"
            renderItem={(props) => renderItem(props, t)}
          />
        )}
      </div>
    </div>
  );
};
